import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as ResetPasswordIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  VpnKey as GeneratePasswordIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { usePermissions } from '../../context/AuthContext';

const UsersList = () => {
  const { can } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    email_local_part: '',
    domain: 'pilvisystems.com',
    password: '',
    first_name: '',
    last_name: '',
    group_ids: [],
    contact_id: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [createResultPassword, setCreateResultPassword] = useState('');
  const [domains, setDomains] = useState(['pilvisystems.com']);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [userGroupsLoading, setUserGroupsLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, search, roleFilter]);

  useEffect(() => {
    if (createDialog || editDialog) {
      fetchDomains();
      fetchUserGroups();
    }
  }, [createDialog, editDialog]);

  const isCustomerGroupSelection = (groupIds) => {
    return userGroups
      .filter((group) => groupIds.includes(group.id))
      .some((group) => (group.roles || []).some((role) => role.code === 'customer'));
  };

  useEffect(() => {
    if (isCustomerGroupSelection(formData.group_ids)) {
      fetchContacts();
    }
  }, [formData.group_ids, userGroups]);

  const fetchUserGroups = async () => {
    setUserGroupsLoading(true);
    try {
      const response = await api.get('/users/groups-list');
      setUserGroups(response.data?.data || []);
    } catch (err) {
      toast.error('Failed to load groups');
      setUserGroups([]);
    } finally {
      setUserGroupsLoading(false);
    }
  };

  const fetchDomains = async () => {
    setDomainsLoading(true);
    try {
      const response = await api.get('/users/domains');
      const list = response.data?.data?.domains || [];
      setDomains(list.length ? list : ['pilvisystems.com']);
      setFormData((prev) => ({
        ...prev,
        domain: prev.domain || list[0] || 'pilvisystems.com'
      }));
    } catch (err) {
      toast.error('Failed to load email domains');
      setDomains(['pilvisystems.com']);
    } finally {
      setDomainsLoading(false);
    }
  };

  const generateStrongAdminPassword = (length = 64) => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%^&*-_=+?';
    const all = upper + lower + digits + symbols;
    const picks = [
      upper[Math.floor(Math.random() * upper.length)],
      lower[Math.floor(Math.random() * lower.length)],
      digits[Math.floor(Math.random() * digits.length)],
      symbols[Math.floor(Math.random() * symbols.length)]
    ];
    const bytes = new Uint8Array(length);
    window.crypto.getRandomValues(bytes);
    while (picks.length < length) {
      picks.push(all[bytes[picks.length % bytes.length] % all.length]);
    }
    for (let i = picks.length - 1; i > 0; i -= 1) {
      const j = bytes[i % bytes.length] % (i + 1);
      [picks[i], picks[j]] = [picks[j], picks[i]];
    }
    return picks.join('');
  };

  const generateClientPassword = () => {
    const password = generateStrongAdminPassword(64);
    setFormData((prev) => ({ ...prev, password }));
    setGeneratedPassword(password);
    return password;
  };

  const generateResetPassword = () => {
    const password = generateStrongAdminPassword(64);
    setNewPassword(password);
    return password;
  };

  const splitEmail = (email) => {
    const normalized = String(email || '').trim().toLowerCase();
    const atIndex = normalized.lastIndexOf('@');
    if (atIndex <= 0) return { local: normalized, domain: 'pilvisystems.com' };
    return {
      local: normalized.slice(0, atIndex),
      domain: normalized.slice(atIndex + 1)
    };
  };

  const isLockedUser = (user) => Boolean(user?.is_system_user || user?.role === 'super_admin');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          role: roleFilter !== 'all' ? roleFilter : undefined
        }
      });
      setUsers(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    setContactsLoading(true);
    try {
      const response = await api.get('/contacts', { params: { page: 1, limit: 500 } });
      const raw = response?.data?.data;
      setContacts(Array.isArray(raw) ? raw : []);
    } catch (err) {
      toast.error('Failed to load contacts');
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        group_ids: formData.group_ids
      };

      if (isCustomerGroupSelection(formData.group_ids) && formData.contact_id) {
        const contact = contacts.find((c) => c.id === formData.contact_id);
        payload.contact_id = formData.contact_id;
        if (contact?.email) {
          payload.email = contact.email;
        }
      } else {
        payload.email_local_part = formData.email_local_part.trim();
        payload.domain = formData.domain;
      }

      if (formData.password) {
        payload.password = formData.password;
      } else {
        payload.generate_password = true;
      }

      const response = await api.post('/users', payload);
      const createdPassword = response.data?.data?.generated_password || formData.password;
      if (createdPassword) {
        setCreateResultPassword(createdPassword);
        toast.success('User created. Copy the temporary password — the user must change it on first login.');
      } else {
        toast.success('User created successfully. The user must change their password on first login.');
        setCreateDialog(false);
        resetForm();
      }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/users/${selectedUser.id}`, {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        group_ids: formData.group_ids,
        is_active: formData.is_active
      });
      toast.success('User updated successfully');
      setEditDialog(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await api.post(`/users/${selectedUser.id}/reset-password`, {
        generate_password: !newPassword,
        ...(newPassword ? { new_password: newPassword } : {})
      });
      const generated = response.data?.data?.generated_password || newPassword;
      if (generated) {
        setNewPassword(generated);
        toast.success('Password reset. Copy the temporary password — user must change it on next login.');
      } else {
        toast.success('Password reset successfully');
        setResetPasswordDialog(false);
        setNewPassword('');
        setSelectedUser(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await api.post(`/users/${user.id}/toggle-status`);
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change status');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      email_local_part: '',
      domain: 'pilvisystems.com',
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      group_ids: (user.accessGroups || []).map((g) => g.id),
      contact_id: user.contact_id || '',
      is_active: user.is_active
    });
    setEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      email_local_part: '',
      domain: domains[0] || 'pilvisystems.com',
      password: '',
      first_name: '',
      last_name: '',
      group_ids: [],
      contact_id: ''
    });
    setSelectedUser(null);
    setGeneratedPassword('');
    setCreateResultPassword('');
  };

  const onContactSelect = (contactId) => {
    const contact = contacts.find((c) => c.id === contactId);
    if (contact) {
      const { local, domain } = splitEmail(contact.email);
      setFormData((prev) => ({
        ...prev,
        contact_id: contactId,
        email_local_part: local || prev.email_local_part,
        domain: domains.includes(domain) ? domain : (domains[0] || prev.domain),
        first_name: contact.first_name || prev.first_name,
        last_name: contact.last_name || prev.last_name
      }));
    } else {
      setFormData((prev) => ({ ...prev, contact_id: contactId }));
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
        return <AdminIcon color="error" />;
      case 'manager':
        return <ManagerIcon color="warning" />;
      case 'customer':
        return <PersonAddIcon color="info" />;
      default:
        return <AdminIcon color="primary" />;
    }
  };

  const getRoleChip = (role) => {
    const config = {
      super_admin: { color: 'error', label: 'Super Admin' },
      manager: { color: 'warning', label: 'Manager' },
      admin: { color: 'primary', label: 'Admin' },
      customer: { color: 'info', label: 'Customer' }
    };
    const c = config[role] || config.admin;
    return <Chip label={c.label} color={c.color} size="small" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          User Management
        </Typography>
        {can('users', 'add') && (
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => { resetForm(); setCreateDialog(true); }}
          sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' }}
        >
          Add User
        </Button>
        )}
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Manage Admin and Manager users. Managers can approve tenant requests. Admins can create and verify tenants.
      </Alert>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search users..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Role</InputLabel>
          <Select
            value={roleFilter}
            label="Role"
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="super_admin">Super Admin</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Groups</TableCell>
              <TableCell>Contact (Customer)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>Loading...</TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>No users found</TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getRoleIcon(user.role)}
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {user.first_name} {user.last_name}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}{user.is_system_user ? (
                    <Chip size="small" label="System" color="warning" sx={{ ml: 1 }} />
                  ) : null}</TableCell>
                  <TableCell>{getRoleChip(user.role)}</TableCell>
                  <TableCell>
                    {(user.accessGroups || []).map((group) => (
                      <Chip key={group.id} label={group.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                    {!user.accessGroups?.length && '—'}
                  </TableCell>
                  <TableCell>
                    {user.role === 'customer' && user.contact
                      ? `${user.contact.first_name || ''} ${user.contact.last_name || ''} (${user.contact.email})`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={user.is_active}
                      onChange={() => handleToggleStatus(user)}
                      disabled={isLockedUser(user)}
                      size="small"
                    />
                    <Typography variant="caption" color={user.is_active ? 'success.main' : 'error.main'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell align="right">
                    {can('users', 'edit') && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(user)} disabled={isLockedUser(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    )}
                    {can('users', 'edit') && (
                    <Tooltip title="Reset Password">
                      <IconButton 
                        size="small" 
                        onClick={() => { setSelectedUser(user); setResetPasswordDialog(true); }}
                        disabled={isLockedUser(user)}
                      >
                        <ResetPasswordIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    )}
                    {can('users', 'delete') && (
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => { setSelectedUser(user); setDeleteDialog(true); }}
                        disabled={isLockedUser(user)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      {/* Create User Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => { if (!createResultPassword) { setCreateDialog(false); resetForm(); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          {createResultPassword ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              User created. Share this temporary password securely — the user must change it on first login:
              <Box sx={{ mt: 1, fontFamily: 'monospace', fontWeight: 700 }}>{createResultPassword}</Box>
            </Alert>
          ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Email (local part)"
                placeholder="firstname.lastname"
                value={formData.email_local_part}
                onChange={(e) => setFormData({ ...formData, email_local_part: e.target.value })}
                required
                disabled={formData.role === 'customer' && Boolean(formData.contact_id)}
              />
              <Typography sx={{ mt: 2.5 }}>@</Typography>
              <FormControl sx={{ minWidth: 180 }} required>
                <InputLabel>Domain</InputLabel>
                <Select
                  value={formData.domain}
                  label="Domain"
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  disabled={domainsLoading || (formData.role === 'customer' && Boolean(formData.contact_id))}
                >
                  {domains.map((domain) => (
                    <MenuItem key={domain} value={domain}>{domain}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Password"
                type="text"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                helperText="Use Generate for a 64-character strong password, or enter one manually (64+ chars with upper, lower, number, symbol). User must change it on first login."
              />
              <Button
                variant="outlined"
                startIcon={<GeneratePasswordIcon />}
                onClick={generateClientPassword}
                sx={{ mt: 1, whiteSpace: 'nowrap' }}
              >
                Generate
              </Button>
            </Box>
            {generatedPassword && (
              <Alert severity="info">Generated password: <strong>{generatedPassword}</strong></Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Box>
            <FormControl fullWidth required>
              <InputLabel>Groups</InputLabel>
              <Select
                multiple
                value={formData.group_ids}
                onChange={(e) => setFormData({ ...formData, group_ids: e.target.value, contact_id: '' })}
                input={<OutlinedInput label="Groups" />}
                disabled={userGroupsLoading}
                renderValue={(selected) =>
                  userGroups.filter((g) => selected.includes(g.id)).map((g) => g.name).join(', ')
                }
              >
                {userGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                    {(group.roles || []).length ? ` (${group.roles.map((r) => r.name).join(', ')})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isCustomerGroupSelection(formData.group_ids) && (
              <FormControl fullWidth required>
                <InputLabel>Contact</InputLabel>
                <Select
                  value={formData.contact_id}
                  label="Contact"
                  onChange={(e) => onContactSelect(e.target.value)}
                  disabled={contactsLoading}
                >
                  <MenuItem value="">Select a contact</MenuItem>
                  {contacts.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} ({c.email})
                      {c.account?.account_name ? ` — ${c.account.account_name}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          )}
        </DialogContent>
        <DialogActions>
          {createResultPassword ? (
            <Button variant="contained" onClick={() => { setCreateDialog(false); resetForm(); }}>
              Done
            </Button>
          ) : (
          <>
          <Button onClick={() => { setCreateDialog(false); resetForm(); }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreate}
            disabled={
              !formData.first_name || !formData.last_name || !formData.group_ids.length ||
              (isCustomerGroupSelection(formData.group_ids)
                ? !formData.contact_id
                : (!formData.email_local_part || !formData.domain))
            }
          >
            Create User
          </Button>
          </>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Box>
            <FormControl fullWidth required>
              <InputLabel>Groups</InputLabel>
              <Select
                multiple
                value={formData.group_ids}
                onChange={(e) => setFormData({ ...formData, group_ids: e.target.value })}
                input={<OutlinedInput label="Groups" />}
                disabled={userGroupsLoading}
                renderValue={(selected) =>
                  userGroups.filter((g) => selected.includes(g.id)).map((g) => g.name).join(', ')
                }
              >
                {userGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedUser?.role === 'customer' && selectedUser?.contact && (
              <Alert severity="info">
                Linked to contact: {selectedUser.contact.first_name} {selectedUser.contact.last_name} ({selectedUser.contact.email})
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.first_name} {selectedUser?.last_name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onClose={() => setResetPasswordDialog(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Set a new 64-character strong password for {selectedUser?.first_name} {selectedUser?.last_name}.
            Leave blank and reset to auto-generate one.
          </Typography>
          <TextField
            fullWidth
            label="New Password"
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="64+ characters with upper, lower, number, and symbol. User must change password on next login."
            multiline
            minRows={2}
          />
          <Button
            variant="outlined"
            startIcon={<GeneratePasswordIcon />}
            onClick={generateResetPassword}
            sx={{ mt: 1 }}
          >
            Generate Password
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetPasswordDialog(false); setNewPassword(''); }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleResetPassword}
            disabled={newPassword.length > 0 && newPassword.length < 64}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersList;
